(() => {
  'use strict';

  const PICKER_SELECTOR = 'variant-selects';
  const INITIALIZED_ATTRIBUTE = 'data-chs-unselected-initialized';
  const PURCHASE_DISABLED_CLASS = 'chs-variant-purchase-disabled';
  const VISUAL_AVAILABILITY_EXEMPT_OPTIONS = new Set([
    'side',
    'stick curve',
    'cote',
    'courbe'
  ]);

  /**
   * Returns the localized message shown before all options are selected.
   *
   * @returns {string}
   */
  const getSelectionMessage = () => {
    const language = document.documentElement.lang.toLowerCase();

    return language.startsWith('fr')
      ? 'Sélectionnez les options'
      : 'Select options';
  };

  /**
   * Returns the closest Shopify section containing the variant picker.
   *
   * @param {HTMLElement} picker
   * @returns {HTMLElement}
   */
  const getPickerScope = (picker) => {
    return (
      picker.closest('.shopify-section') ||
      picker.closest('section') ||
      picker.closest('product-info') ||
      picker.parentElement
    );
  };

  /**
   * Returns all option groups belonging to a variant picker.
   *
   * @param {HTMLElement} picker
   * @returns {HTMLElement[]}
   */
  const getOptionGroups = (picker) => {
    return Array.from(
      picker.querySelectorAll('.product-form__input')
    ).filter((group) => {
      return group.querySelector(
        'input[type="radio"], select'
      );
    });
  };

  /**
   * Returns the currently selected value for one option group.
   *
   * @param {HTMLElement} group
   * @returns {string|undefined}
   */
  const getGroupValue = (group) => {
    const select = group.querySelector('select');

    if (select) {
      return select.value || undefined;
    }

    return group.querySelector(
      'input[type="radio"]:checked'
    )?.value;
  };

  /**
   * Keeps inventory marks off top-level customizer choices that
   * should stay explorable while lower-level options show stock.
   *
   * @param {HTMLElement} group
   * @returns {boolean}
   */
  const ignoresVisualAvailability = (group) => {
    const control = group.querySelector(
      'input[type="radio"], select'
    );

    const optionName = control?.name
      ?.replace(/^options\[/, '')
      .replace(/\]$/, '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();

    return VISUAL_AVAILABILITY_EXEMPT_OPTIONS.has(
      optionName
    );
  };

  const getNormalizedOptionName = (group) => {
    const control = group.querySelector(
      'input[type="radio"], select'
    );

    return control?.name
      ?.replace(/^options\[/, '')
      .replace(/\]$/, '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase() || '';
  };

  const isFlexGroup = (group) => {
    return getNormalizedOptionName(group).includes('flex');
  };

  const allowsNotifyMeSoldOutSelection = (picker) => {
    return picker.dataset.chsNotifyMeSelectable === 'true';
  };

  const isSideGroup = (group) => {
    const optionName = getNormalizedOptionName(group);

    return optionName === 'side' || optionName === 'cote';
  };

  const isCurveGroup = (group) => {
    const optionName = getNormalizedOptionName(group);

    return optionName === 'stick curve' || optionName === 'courbe';
  };

  const hasRequiredFlexParents = (groups) => {
    const sideGroup = groups.find(isSideGroup);
    const curveGroup = groups.find(isCurveGroup);

    return Boolean(
      sideGroup &&
        curveGroup &&
        getGroupValue(sideGroup) &&
        getGroupValue(curveGroup)
    );
  };

  const setFlexGroupVisibility = (group, visible) => {
    group.hidden = !visible;
    group.style.display = visible ? '' : 'none';

    if (visible) {
      return;
    }

    group
      .querySelectorAll('input[type="radio"]:checked')
      .forEach((input) => {
        input.checked = false;
        input.removeAttribute('checked');
      });

    const labelValue = group.querySelector(
      '.form__label-value, [data-selected-swatch-value]'
    );

    if (labelValue) {
      labelValue.textContent = '';
    }
  };

  const setElementHidden = (element, hidden) => {
    element.classList.toggle('chs-option-hidden', hidden);

    if (element.tagName === 'OPTION') {
      element.hidden = hidden;
      return;
    }

    const group = element.closest('.product-form__input');
    const label = element.id
      ? group?.querySelector(
          `label[for="${CSS.escape(element.id)}"]`
        )
      : null;

    if (!label) {
      return;
    }

    label.hidden = hidden;
    label.style.display = hidden ? 'none' : '';
  };

  /**
   * Determines whether every product option has a selection.
   *
   * @param {HTMLElement} picker
   * @returns {boolean}
   */
  const hasCompleteSelection = (picker) => {
    const groups = getOptionGroups(picker);

    return (
      groups.length > 0 &&
      groups.every((group) => Boolean(getGroupValue(group)))
    );
  };

  /**
   * Returns the main and sticky product form wrappers.
   *
   * @param {HTMLElement} picker
   * @returns {HTMLElement[]}
   */
  const getProductForms = (picker) => {
    const sectionId = picker.dataset.section;

    if (!sectionId) {
      return [];
    }

    return [
      document.getElementById(`product-form-${sectionId}`),
      document.getElementById(`sticky-atc-${sectionId}`)
    ].filter(Boolean);
  };

  /**
   * Clears the hidden Shopify variant ID.
   *
   * @param {HTMLElement} picker
   */
  const clearVariantId = (picker) => {
    getProductForms(picker).forEach((form) => {
      const variantInput = form.querySelector(
        'input[name="id"]'
      );

      if (variantInput) {
        variantInput.value = '';
      }
    });
  };

  /**
   * Disables product purchase buttons while selection is incomplete.
   *
   * @param {HTMLElement} picker
   * @param {boolean} useSelectionMessage
   */
  const disablePurchasing = (
    picker,
    useSelectionMessage = true
  ) => {
    const scope = getPickerScope(picker);
    const message = getSelectionMessage();

    if (scope) {
      scope.classList.add(PURCHASE_DISABLED_CLASS);
    }

    getProductForms(picker).forEach((form) => {
      const button = form.querySelector('[name="add"]');

      if (!button) {
        return;
      }

      button.disabled = true;
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-disabled', 'true');

      if (!useSelectionMessage) {
        return;
      }

      const buttonText = button.querySelector('span');

      if (buttonText) {
        buttonText.textContent = message;
      }
    });
  };

  /**
   * Restores purchasing after Shopify confirms a valid variant.
   *
   * Must undo EVERYTHING disablePurchasing set. The theme's own
   * toggleAddButton only clears the `disabled` attribute — it never
   * touches `aria-disabled`, and product-form.js silently ignores
   * every click while aria-disabled="true". Without this cleanup the
   * button looks enabled but add-to-cart never fires.
   *
   * @param {HTMLElement} picker
   */
  const restorePurchasing = (picker) => {
    const scope = getPickerScope(picker);

    if (scope) {
      scope.classList.remove(PURCHASE_DISABLED_CLASS);
    }

    getProductForms(picker).forEach((form) => {
      const button = form.querySelector('[name="add"]');

      if (!button) {
        return;
      }

      button.disabled = false;
      button.removeAttribute('disabled');
      button.removeAttribute('aria-disabled');
    });
  };

  /**
   * Patches the theme's VariantSelects component so partial selections work.
   */
  const patchVariantSelects = () => {
    const VariantSelects = customElements.get(
      'variant-selects'
    );

    if (!VariantSelects) {
      return;
    }

    const prototype = VariantSelects.prototype;

    if (prototype.chsUnselectedVariantsPatched) {
      return;
    }

    prototype.chsUnselectedVariantsPatched = true;

    const originalSetUnavailable =
      prototype.setUnavailable;

    const originalToggleAddButton =
      prototype.toggleAddButton;

    /**
     * Update labels without assuming every option is selected.
     */
    prototype.updateVariantText = function () {
      getOptionGroups(this).forEach((group) => {
        const value = getGroupValue(group) || '';

        const labelValue = group.querySelector(
          '.form__label-value, [data-selected-swatch-value]'
        );

        if (labelValue) {
          labelValue.textContent = value;
        }
      });
    };

    /**
     * Recalculate availability without requiring the first option
     * to already be selected.
     */
    prototype.updateVariantStatuses = function () {
      const variants = this.getVariantData();
      const groups = getOptionGroups(this);
      const showFlexGroups = hasRequiredFlexParents(groups);
      const allowSoldOutSelection =
        allowsNotifyMeSoldOutSelection(this);

      const selectedValues = groups.map((group) => {
        return getGroupValue(group);
      });

      groups.forEach((group, optionIndex) => {
        const ignoreVisualAvailability =
          ignoresVisualAvailability(group);
        const hideNonChildOptions = isFlexGroup(group);

        if (hideNonChildOptions) {
          setFlexGroupVisibility(group, showFlexGroups);
        }

        const optionElements = Array.from(
          group.querySelectorAll(
            'input[type="radio"], option'
          )
        );

        optionElements.forEach((element) => {
          const value = element.value;

          if (!value) {
            return;
          }

          const matchingVariants = variants.filter(
            (variant) => {
              if (
                variant.options[optionIndex] !== value
              ) {
                return false;
              }

              return selectedValues.every(
                (selectedValue, selectedIndex) => {
                  if (
                    !selectedValue ||
                    selectedIndex === optionIndex
                  ) {
                    return true;
                  }

                  return (
                    variant.options[selectedIndex] ===
                    selectedValue
                  );
                }
              );
            }
          );

          const isChildOption =
            ignoreVisualAvailability ||
            matchingVariants.length > 0;
          const hasAvailableVariant =
            matchingVariants.some((variant) => {
              return variant.available;
            });
          const isSelectable =
            ignoreVisualAvailability ||
            hasAvailableVariant ||
            (allowSoldOutSelection && isChildOption);
          const isSoldOutSelectable =
            allowSoldOutSelection &&
            !ignoreVisualAvailability &&
            matchingVariants.length > 0 &&
            !hasAvailableVariant;
          const isHidden =
            hideNonChildOptions && !isChildOption;

          if (element.tagName === 'INPUT') {
            element.classList.toggle(
              'disabled',
              !isSelectable
            );
            element.classList.toggle(
              'chs-sold-out-selectable',
              isSoldOutSelectable
            );
            setElementHidden(element, isHidden);

            element.setAttribute(
              'aria-disabled',
              String(!isSelectable)
            );
          }

          if (element.tagName === 'OPTION') {
            element.disabled = !isSelectable;
            element.classList.toggle(
              'disabled',
              !isSelectable
            );
            element.classList.toggle(
              'chs-sold-out-selectable',
              isSoldOutSelectable
            );
            setElementHidden(element, isHidden);

            if (isSelectable) {
              element.textContent = value;
            } else if (
              window.variantStrings
                ?.unavailable_with_option
            ) {
              element.textContent =
                window.variantStrings.unavailable_with_option.replace(
                  '[value]',
                  value
                );
            }
          }
        });
      });
    };

    /**
     * Distinguish an incomplete selection from an unavailable variant.
     */
    prototype.setUnavailable = function () {
      if (!hasCompleteSelection(this)) {
        clearVariantId(this);
        disablePurchasing(this, true);
        return;
      }

      disablePurchasing(this, false);

      if (
        typeof originalSetUnavailable === 'function'
      ) {
        return originalSetUnavailable.call(this);
      }
    };

    /**
     * Restore accelerated checkout only after Shopify enables
     * the Add to Cart button for a valid variant.
     */
    prototype.toggleAddButton = function (
      disable = true,
      text,
      modifyClass = true
    ) {
      const result =
        originalToggleAddButton.call(
          this,
          disable,
          text,
          modifyClass
        );

      if (
        disable === false &&
        this.currentVariant
      ) {
        restorePurchasing(this);
      }

      return result;
    };
  };

  /**
   * Removes Shopify's server-rendered default selection.
   *
   * @param {HTMLElement} picker
   */
  const initializePicker = (picker) => {
    if (
      picker.hasAttribute(INITIALIZED_ATTRIBUTE)
    ) {
      return;
    }

    picker.setAttribute(
      INITIALIZED_ATTRIBUTE,
      'true'
    );

    /*
     * Preserve explicitly linked variants:
     * /products/example?variant=123456789
     */
    const hasExplicitVariant =
      window.location.search.includes('variant=');

    if (hasExplicitVariant) {
      return;
    }

    const radioGroups = getOptionGroups(
      picker
    ).filter((group) => {
      return group.querySelector(
        'input[type="radio"]'
      );
    });

    if (!radioGroups.length) {
      return;
    }

    radioGroups.forEach((group) => {
      group
        .querySelectorAll(
          'input[type="radio"]:checked'
        )
        .forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });

      group
        .querySelectorAll('.selected')
        .forEach((element) => {
          element.classList.remove('selected');
        });

      const labelValue = group.querySelector(
        '.form__label-value, [data-selected-swatch-value]'
      );

      if (labelValue) {
        labelValue.textContent = '';
      }
    });

    picker.currentVariant = undefined;

    clearVariantId(picker);
    disablePurchasing(picker, true);

    if (
      typeof picker.updateOptions === 'function'
    ) {
      picker.updateOptions();
    }

    if (
      typeof picker.updateVariantStatuses ===
      'function'
    ) {
      picker.updateVariantStatuses();
    }

    if (
      typeof picker.updateVariantText ===
      'function'
    ) {
      picker.updateVariantText();
    }
  };

  /**
   * Initializes existing and dynamically inserted variant pickers.
   */
  const initializeAllPickers = () => {
    document
      .querySelectorAll(PICKER_SELECTOR)
      .forEach(initializePicker);
  };

  /**
   * Adds the accelerated-checkout protection styling.
   */
  const injectStyles = () => {
    if (
      document.getElementById(
        'chs-unselected-variants-styles'
      )
    ) {
      return;
    }

    const style = document.createElement('style');

    style.id =
      'chs-unselected-variants-styles';

    style.textContent = `
      .${PURCHASE_DISABLED_CLASS} .shopify-payment-button,
      .${PURCHASE_DISABLED_CLASS} shopify-accelerated-checkout,
      .${PURCHASE_DISABLED_CLASS} shopify-buy-it-now-button,
      .${PURCHASE_DISABLED_CLASS} [data-shopify="payment-button"] {
        display: none !important;
      }
    `;

    document.head.appendChild(style);
  };

  /**
   * Starts the global behavior.
   */
  const boot = () => {
    injectStyles();
    patchVariantSelects();
    initializeAllPickers();

    const observer = new MutationObserver(
      (mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) {
              return;
            }

            if (node.matches(PICKER_SELECTOR)) {
              initializePicker(node);
            }

            node
              .querySelectorAll?.(PICKER_SELECTOR)
              .forEach(initializePicker);
          });
        });
      }
    );

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  };

  if (customElements.get('variant-selects')) {
    boot();
  } else {
    customElements
      .whenDefined('variant-selects')
      .then(boot);
  }
})();
